import React, { useEffect, useState } from "react";
import {
  StyledSelectYearContainer,
  StyledCollaboratorDetail,
  StyledTabContainer,
} from "./CollaboratorProfileTabs.styled";
import { CollaboratorProfileCards } from "../../../screens/CollaboratorDetail/CollaboratorProfileCards/CollaboratorProfileCards";
import {
  CM_SHEET_LABEL,
  CURRICULUM_VITAE_LABEL,
  DOWNLOAD_FORMAT_LABEL,
  DOWNLOAD_IT_LABEL,
  EMPTY_CURRICULUM_MESSAGE,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  PROFESSIONAL_HISTORY_LABEL,
  RESUME_LABEL,
} from "../../../constants/Dictionary";
import {
  TabMenuCollaborator,
  ProfessionalHistoryMenuEnum,
  CollaboratorContractTypeEnum,
  DownloadFormatEnum,
  DOWNLOAD_FORMAT,
  PermissionsEnum,
} from "../../../constants/AppConstants";
import { ReactComponent as BriefcaseIcon } from "../../../icons/briefcase.svg";
import { ReactComponent as FileCheckIcon } from "../../../icons/fileCheck.svg";
import { ReactComponent as GraduationIcon } from "../../../icons/graduation.svg";
import { ReactComponent as CvIcon } from "../../../icons/cv.svg";
import { ReactComponent as DownloadDocIcon } from "../../../icons/downloadDoc.svg";
import { InputSizes } from "../../../components/core/Input/Input";
import { Loader } from "../../../components/core/Loader/Loader";
import {
  Select,
  SelectVariantEnum,
} from "../../../components/core/Select/Select";
import {
  ExpandMenu,
  ExpandMenuOptionItem,
} from "../../../components/ExpandMenu/ExpandMenu";
import { Tab } from "../../../components/Tab/Tab";
import { useAppSelector } from "../../../hooks/store.hook";
import { FontWeightEnum, TabMenu } from "../../../types/AppTypes";
import { getProfessionalHistoryMenu } from "../CollaboratorDetail";
import { ProfessionalHistory } from "./ProfessionalHistory/ProfessionalHistory";
import { Resume } from "./Resume/Resume";
import SchedaCM from "./SchedaCM/SchedaCM";
import LoadingThumbnail from "../../../components/LoadingThumbnail/LoadingThumbnail";
import { useEditing } from "../../../providers/editing.provider";
import { Button, ButtonSizes } from "../../../components/core/Button/Button";
import { Modal } from "../../../components/core/Modal/Modal";
import { ModalSelectRadio } from "../../../components/ModalSelectRadio/ModalSelectRadio";
import { PDFViewer } from "../../../components/PdfViewer/PdfViewer";
import { BodyTyp } from "../../../components/Typography/Typography";
import { useNotification } from "../../../hooks/notification.hook";
import { roleHasPermission } from "../../../utils/roleAuth.utils";
import { useParams } from "react-router-dom";
import {
  useCollaboratorAdvancements,
  useCollaboratorApprovalStatus,
  useCollaboratorCmSheets,
  useCollaboratorCurriculum,
  useCollaboratorDetails,
  useDownloadCollaboratorCv,
} from "../../../hooks/collaboratorProfile.hook";
import { isProd, isQA } from "../../../utils/generals.utils";

type CollaboratorProfileTabsProps = {
  isMyProfile?: boolean;
};

export const CollaboratorProfileTabs = ({
  isMyProfile = false,
}: CollaboratorProfileTabsProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const { confirmLeave, edit } = useEditing();
  const { collaboratorId } = useParams();
  const { notification } = useNotification();
  const [professionalHistorySelectedMenu, setProfessionalHistorySelectedMenu] =
    useState<ExpandMenuOptionItem<ProfessionalHistoryMenuEnum>>(
      getProfessionalHistoryMenu(isMyProfile)[0],
    );
  const [tabSelected, setTabSelected] = useState<number>(
    TabMenuCollaborator.PROFESSIONAL_HISTORY,
  );
  const [yearSelected, setYearSelected] = useState<number | null>(null);
  const [expandTab, setExpandTab] = useState<boolean>(false);
  const [printButton, setPrintButton] = useState<React.ReactNode | null>(null);
  const [isOpenFormatDownloadModal, setIsOpenFormatDownloadModal] =
    useState<boolean>(false);
  const [showCvModal, setShowCvModal] = useState<boolean>(false);

  const profileId = isMyProfile ? profile.id : Number(collaboratorId);

  const tabMenu: TabMenu[] = [
    {
      id: TabMenuCollaborator.PROFESSIONAL_HISTORY,
      label: PROFESSIONAL_HISTORY_LABEL,
      icon: <BriefcaseIcon />,
    },
    {
      id: TabMenuCollaborator.CM_SHEET,
      label: CM_SHEET_LABEL,
      icon: <GraduationIcon />,
    },
    ...(isProd() || isQA()
      ? []
      : [
          {
            id: TabMenuCollaborator.RESUME,
            label: RESUME_LABEL,
            icon: <FileCheckIcon />,
          },
        ]),
  ];

  const handleProfileTabSwitch = (tab: number) => {
    if (tab !== TabMenuCollaborator.RESUME) {
      setPrintButton(null);
    }
    setTabSelected(tab);
  };

  const handleProfessionalHistoryMenuSwitch = (
    menu: ExpandMenuOptionItem<ProfessionalHistoryMenuEnum>,
  ) => {
    setProfessionalHistorySelectedMenu(menu);
  };

  const { data: collaboratorCmSheets, isFetching: cmSheetsLoading } =
    useCollaboratorCmSheets(profileId);

  const { data: collaboratorData, isFetching: collaboratorLoading } =
    useCollaboratorDetails(profileId);

  const currentCmSheet = collaboratorCmSheets?.find((item) => item.isCurrent);

  const isHistoryStatsEnabled = () => {
    if (isMyProfile) {
      return true;
    }

    if (currentCmSheet?.careerManagerId === profile.id) {
      return true;
    }

    if (
      roleHasPermission(profile, PermissionsEnum.ENABLE_HISTORY_STATS) &&
      collaboratorData?.terminationDate === null
    ) {
      return true;
    }

    return false;
  };

  const { data: approvalStatusData, refetch: refetchApprovalStatus } =
    useCollaboratorApprovalStatus(profileId, isHistoryStatsEnabled());

  const { downloadCv, isDownloading } = useDownloadCollaboratorCv();

  const handleDownloadCv = async (format: string) => {
    if (!collaboratorData) return;
    try {
      await downloadCv({ collaboratorId: collaboratorData.id, format });
    } catch (error) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    } finally {
      setIsOpenFormatDownloadModal(false);
    }
  };

  const filteredTabs =
    collaboratorCmSheets?.length === 0
      ? tabMenu.filter(
          (tab) =>
            tab.id !== TabMenuCollaborator.RESUME &&
            tab.id !== TabMenuCollaborator.CM_SHEET,
        )
      : isMyProfile
        ? tabMenu.filter((tab) => tab.id !== TabMenuCollaborator.RESUME)
        : tabMenu;

  const configYears = collaboratorCmSheets
    ? collaboratorCmSheets
        .map((item) => item.configYear)
        .sort((a, b) => b - a)
        .map((year) => ({
          value: year,
          label: year.toString(),
        }))
    : [];

  const cmSheetYearSelected = collaboratorCmSheets?.find(
    (item) => item.configYear === yearSelected,
  );

  const isInterim =
    collaboratorData?.contractType === CollaboratorContractTypeEnum.INT;

  useEffect(() => {
    if (!yearSelected && configYears.length > 0) {
      setYearSelected(configYears[0].value);
    }
  }, [configYears, yearSelected]);

  const handleOnChange = (value: number | null) => {
    if (edit) {
      confirmLeave(() => setYearSelected(value));
    } else {
      setYearSelected(value);
    }
  };

  const { data: file, isFetching: loading } = useCollaboratorCurriculum(
    collaboratorData?.id,
    collaboratorData != null && showCvModal,
  );

  const rightContentTab = () => {
    if (tabSelected === TabMenuCollaborator.PROFESSIONAL_HISTORY) {
      let enable = false;

      if (
        !isMyProfile &&
        approvalStatusData?.totalSectionsCounts.pending === 0
      ) {
        if (currentCmSheet?.careerManagerId === profile.id) {
          enable = true;
        }

        if (
          roleHasPermission(
            profile,
            PermissionsEnum.CAN_DOWNLOAD_CV_COLLABORATOR_PROFILE,
          ) &&
          collaboratorData?.terminationDate === null
        ) {
          enable = true;
        }
      }

      if (!enable) {
        return null;
      }

      return (
        <Button
          size={ButtonSizes.SMALL}
          label={CURRICULUM_VITAE_LABEL}
          icon={<CvIcon />}
          onClick={() => setShowCvModal(true)}
        />
      );
    }
    if (tabSelected === TabMenuCollaborator.CM_SHEET) {
      return (
        <StyledSelectYearContainer>
          <Select
            variant={SelectVariantEnum.DEFAULT}
            onChange={(value) => handleOnChange(value)}
            size={InputSizes.SMALL}
            options={configYears}
            value={yearSelected}
            allowClear={false}
          />
        </StyledSelectYearContainer>
      );
    }
    if (tabSelected === TabMenuCollaborator.RESUME) {
      return printButton;
    }
    return null;
  };

  const { data: advancementsData, isFetching: loadingAdvancement } =
    useCollaboratorAdvancements(profileId);

  const loadingDetails = cmSheetsLoading || collaboratorLoading;

  const showDetails = !loadingDetails && collaboratorData;

  return (
    <>
      <StyledCollaboratorDetail>
        {loadingDetails ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : null}
        {showDetails ? (
          <>
            <CollaboratorProfileCards
              expanded={expandTab}
              isMyProfile={isMyProfile}
              isInterim={isInterim}
              currentCmSheet={currentCmSheet}
              collaboratorAdvancement={advancementsData ?? null}
              loading={loadingAdvancement}
              collaborator={collaboratorData}
            />
            <Tab
              tabSelected={tabSelected}
              setTabSelected={handleProfileTabSwitch}
              tabMenu={filteredTabs}
              confirmLeave={confirmLeave}
              expandControl={{
                expanded: expandTab,
                toggle: () => setExpandTab((prev) => !prev),
              }}
              rightContent={rightContentTab()}
            >
              {tabSelected === TabMenuCollaborator.PROFESSIONAL_HISTORY && (
                <StyledTabContainer>
                  <ExpandMenu
                    data={getProfessionalHistoryMenu(
                      isMyProfile,
                      approvalStatusData,
                    )}
                    menuSelected={professionalHistorySelectedMenu}
                    setMenuSelected={handleProfessionalHistoryMenuSwitch}
                  />
                  <ProfessionalHistory
                    collaborator={collaboratorData}
                    selectedMenu={professionalHistorySelectedMenu.value}
                    isMyProfile={isMyProfile}
                    currentCmSheet={currentCmSheet}
                    approvalStatusData={approvalStatusData ?? null}
                    refetchStatus={refetchApprovalStatus}
                  />
                </StyledTabContainer>
              )}
              {tabSelected === TabMenuCollaborator.CM_SHEET &&
                cmSheetYearSelected &&
                yearSelected && (
                  <SchedaCM
                    isMyProfile={isMyProfile}
                    cmSheetInfo={cmSheetYearSelected}
                    collaborator={collaboratorData}
                    isInterim={isInterim}
                  />
                )}
              {tabSelected === TabMenuCollaborator.RESUME && (
                <StyledTabContainer>
                  <Resume
                    cmSheetsLoading={cmSheetsLoading}
                    collaboratorCmSheets={collaboratorCmSheets ?? null}
                    printButton={setPrintButton}
                    collaboratorId={collaboratorData.id}
                    collaboratorData={collaboratorData}
                  />
                </StyledTabContainer>
              )}
            </Tab>
          </>
        ) : null}
      </StyledCollaboratorDetail>
      <Modal
        label={CURRICULUM_VITAE_LABEL}
        isOpen={showCvModal}
        closeModal={() => setShowCvModal(false)}
        rightButton={
          <Button
            label={DOWNLOAD_IT_LABEL}
            onClick={() => {
              setIsOpenFormatDownloadModal(true);
              setShowCvModal(false);
            }}
            icon={<DownloadDocIcon />}
          />
        }
      >
        {loading ? (
          <Loader loading size={18} />
        ) : file ? (
          <PDFViewer file={file.slice(0)} />
        ) : (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {EMPTY_CURRICULUM_MESSAGE}
          </BodyTyp>
        )}
      </Modal>
      <ModalSelectRadio<string>
        label={DOWNLOAD_FORMAT_LABEL}
        isOpen={isOpenFormatDownloadModal}
        radioSelectionItems={[
          DownloadFormatEnum.PDF,
          DownloadFormatEnum.WORD,
          DownloadFormatEnum.PPT,
        ]}
        onConfirm={(format: string | null) => {
          if (format) {
            const apiFormat = DOWNLOAD_FORMAT[format as DownloadFormatEnum];
            handleDownloadCv(apiFormat);
          }
        }}
        onCancel={() => setIsOpenFormatDownloadModal(false)}
        isLoading={isDownloading}
      />
    </>
  );
};
